import { PropsWithChildren, useRef, useState } from 'react';
import styled from 'styled-components';
import { useButton } from '@react-aria/button';

import { colors, s4, s8 } from '@ardoq/design-tokens';
import { AnimatedChevron } from '@ardoq/icons';
import { focusRingCSS } from '@ardoq/style-helpers';
import { text2Bold } from '@ardoq/typography';

const DetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${s8};
  color: ${colors.textDefault};
`;

const DetailsHeader = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const ExpanderButton = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: ${s4};
  padding-left: ${s8};
  ${text2Bold}
  color: ${colors.textModerate};
  cursor: pointer;
  user-select: none;

  &:focus {
    outline: none;
    ${focusRingCSS}
  }
`;

type DetailsProps = PropsWithChildren<{
  label?: string;
  isInitiallyOpen?: boolean;
  hasExpanderButton?: boolean;
}>;
export const NotificationDetails = ({
  label = 'Details',
  isInitiallyOpen = false,
  hasExpanderButton = true,
  children,
}: DetailsProps) => {
  const [isExpanded, setExpanded] = useState(
    isInitiallyOpen || !hasExpanderButton
  );

  // Make the "button" a button
  const ref = useRef(null);
  const { buttonProps } = useButton(
    {
      elementType: 'div',
      onPress: () => setExpanded(!isExpanded),
    },
    ref
  );

  return (
    <DetailsContainer>
      {hasExpanderButton && (
        <DetailsHeader>
          <ExpanderButton ref={ref} {...buttonProps}>
            {label}
            <AnimatedChevron $isUp={isExpanded} />
          </ExpanderButton>
        </DetailsHeader>
      )}
      {isExpanded && children}
    </DetailsContainer>
  );
};
